import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { RegistrationBillingInterval, RegistrationPlanTier } from '@/modules/user/services/registrationPlans'
import {
  normalizeAgencyPlanOptions,
  resolveAgencyPlanDefaultSelection,
  type AgencyPlanApiResponse,
  type AgencyPlanOption
} from '@/modules/settings/services/agencyPricingOptions'
import api from '@/services/axiosApi'
import { useAuthStore } from './authStore'
import { useNotificationStore } from './notificationStore'

export interface AgencyOffer {
  id: number
  code: string
  name: string
  description?: string
  pricing_display: string
  pricing_type: string
  headline?: string
  subheadline?: string
  benefits?: { benefit: string }[]
}

export interface AgencyClient {
  id: number
  email: string
  name: string
  status: 'active' | 'trial' | 'expired' | 'lifetime' | string
  subscription_status?: string
  team_name?: string
  team_pid?: string
  trial_ends_at?: string
  created_at: string
}

export interface AgencyInvitation {
  id: number
  email: string
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  offer_name?: string | null
  plan_tier?: RegistrationPlanTier | null
  billing_interval?: RegistrationBillingInterval | null
  expires_at: string
  accepted_at?: string
  created_at: string
  signup_url: string
}

export interface AgencyPlansState {
  plans: AgencyPlanOption[]
  defaultTier: RegistrationPlanTier | null
  defaultInterval: RegistrationBillingInterval | null
}

export interface AgencyProfile {
  name: string | null
  logo: string | null
  welcome_message: string | null
  website: string | null
  tagline: string | null
  description: string | null
  location: string | null
  services_offered: string[]
  esps_proficient_in: string[]
  is_public: boolean
  first_promoter_ref_id: string | null
  signup_url: string | null
  available_offers: AgencyOffer[]
}

export interface AffiliateStats {
  configured: boolean
  reward_tier: string | null
  commission_percentage: number | null
}

export interface AgencyOption {
  value: string
  label: string
}

export interface AgencyOptions {
  services: AgencyOption[]
  esps: AgencyOption[]
  countries: AgencyOption[]
}

export const useAgencyStore = defineStore('agency', () => {
  const authStore = useAuthStore()
  const notificationStore = useNotificationStore()
  const queryClient = useQueryClient()
  const { user } = storeToRefs(authStore)

  const isAgency = computed(() => user.value?.is_agency ?? false)

  const profileQuery = useQuery({
    queryKey: ['agency', 'profile'],
    queryFn: () =>
      api.get<{ status: string; data: AgencyProfile }>('/api/agency/profile')
        .then((res) => res.data.data),
    enabled: () => isAgency.value
  })

  const offersQuery = useQuery({
    queryKey: ['agency', 'offers'],
    queryFn: () =>
      api.get<{ status: string; data: AgencyOffer[] }>('/api/agency/offers')
        .then((res) => res.data.data),
    enabled: () => isAgency.value
  })

  const clientsQuery = useQuery({
    queryKey: ['agency', 'clients'],
    queryFn: () =>
      api.get<{ status: string; data: { clients: AgencyClient[] } }>('/api/agency/clients')
        .then((res) => res.data.data.clients),
    enabled: () => isAgency.value
  })

  const invitationsQuery = useQuery({
    queryKey: ['agency', 'invitations'],
    queryFn: () =>
      api.get<{ status: string; data: { invitations: AgencyInvitation[] } }>('/api/agency/invitations')
        .then((res) => res.data.data.invitations),
    enabled: () => isAgency.value
  })

  const planTiersQuery = useQuery({
    queryKey: ['agency', 'plans'],
    queryFn: () =>
      api.get<{
        status: string
        data: {
          plans: AgencyPlanApiResponse[]
          default_tier: RegistrationPlanTier | null
          default_interval: RegistrationBillingInterval | null
        }
      }>('/api/agency/plans')
        .then((res) => ({
          plans: normalizeAgencyPlanOptions(res.data.data.plans),
          defaultTier: res.data.data.default_tier ?? null,
          defaultInterval: res.data.data.default_interval ?? null
        }) satisfies AgencyPlansState),
    enabled: () => isAgency.value
  })

  const affiliateReferralsQuery = useQuery({
    queryKey: ['agency', 'affiliate-referrals'],
    queryFn: () =>
      api.get<{ status: string; data: { emails: string[] } }>('/api/agency/affiliate-referrals')
        .then((res) => res.data.data.emails),
    enabled: () => isAgency.value
  })

  const affiliateStatsQuery = useQuery({
    queryKey: ['agency', 'affiliate-stats'],
    queryFn: () =>
      api.get<{ status: string; data: AffiliateStats }>('/api/agency/affiliate-stats')
        .then((res) => res.data.data),
    enabled: () => isAgency.value
  })

  const optionsQuery = useQuery({
    queryKey: ['agency', 'options'],
    queryFn: () =>
      api.get<{ status: string; data: AgencyOptions }>('/api/agency/options')
        .then((res) => res.data.data),
    enabled: () => isAgency.value,
    staleTime: 1000 * 60 * 60
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: {
      name?: string
      logo?: string
      welcome_message?: string
      website?: string
      tagline?: string
      description?: string
      location?: string
      services_offered?: string[]
      esps_proficient_in?: string[]
      is_public?: boolean
    }) => api.put('/api/agency/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'profile'] })
    },
    onError: () => {
      notificationStore.addError('Failed to update agency profile')
    }
  })

  const createInvitationMutation = useMutation({
    mutationFn: (data: {
      email: string
      plan_tier?: RegistrationPlanTier
      billing_interval?: RegistrationBillingInterval
    }) => api.post('/api/agency/invitations', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'invitations'] })
      notificationStore.addNotification('Invitation sent successfully')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to send invitation'
      notificationStore.addError(message)
    }
  })

  const cancelInvitationMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/agency/invitations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'invitations'] })
      notificationStore.addNotification('Invitation cancelled')
    },
    onError: () => {
      notificationStore.addError('Failed to cancel invitation')
    }
  })

  const resendInvitationMutation = useMutation({
    mutationFn: (id: number) => api.post(`/api/agency/invitations/${id}/resend`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'invitations'] })
      notificationStore.addNotification('Invitation resent successfully')
    },
    onError: () => {
      notificationStore.addError('Failed to resend invitation')
    }
  })

  const profile = computed(() => profileQuery.data.value)
  const offers = computed(() => offersQuery.data.value || [])
  const clients = computed(() => clientsQuery.data.value || [])
  const invitations = computed(() => invitationsQuery.data.value || [])
  const pendingInvitations = computed(() => invitations.value.filter((inv) => inv.status === 'pending'))
  const planTiers = computed(() => planTiersQuery.data.value)
  const signupPlans = computed(() => planTiers.value?.plans ?? [])
  const defaultSignupSelection = computed(() =>
    resolveAgencyPlanDefaultSelection(
      signupPlans.value,
      planTiers.value?.defaultTier ?? null,
      planTiers.value?.defaultInterval ?? null
    )
  )
  const affiliateReferredEmails = computed(() => affiliateReferralsQuery.data.value || [])
  const affiliateStats = computed(() => affiliateStatsQuery.data.value)
  const options = computed(() => optionsQuery.data.value)
  const serviceOptions = computed(() => options.value?.services || [])
  const espOptions = computed(() => options.value?.esps || [])
  const countryOptions = computed(() => options.value?.countries || [])

  function isClientReferred(email: string): boolean {
    return affiliateReferredEmails.value.includes(email.toLowerCase())
  }

  function updateProfile(data: {
    name?: string
    logo?: string
    welcome_message?: string
    website?: string
    tagline?: string
    description?: string
    location?: string
    services_offered?: string[]
    esps_proficient_in?: string[]
    is_public?: boolean
  }) {
    return updateProfileMutation.mutateAsync(data)
  }

  function createInvitation(
    email: string,
    options?: {
      plan_tier?: RegistrationPlanTier
      billing_interval?: RegistrationBillingInterval
    }
  ) {
    return createInvitationMutation.mutateAsync({
      email,
      ...options
    })
  }

  function cancelInvitation(id: number) {
    return cancelInvitationMutation.mutateAsync(id)
  }

  function resendInvitation(id: number) {
    return resendInvitationMutation.mutateAsync(id)
  }

  function refetchAll() {
    queryClient.invalidateQueries({ queryKey: ['agency'] })
  }

  return {
    isAgency,
    profile,
    offers,
    clients,
    invitations,
    pendingInvitations,
    planTiers,
    signupPlans,
    defaultSignupSelection,
    affiliateReferredEmails,
    affiliateStats,
    options,
    serviceOptions,
    espOptions,
    countryOptions,

    isLoadingProfile: profileQuery.isLoading,
    isLoadingOffers: offersQuery.isLoading,
    isLoadingClients: clientsQuery.isLoading,
    isLoadingInvitations: invitationsQuery.isLoading,
    isLoadingPlanTiers: planTiersQuery.isLoading,
    isLoadingSignupPlans: planTiersQuery.isLoading,
    isLoadingAffiliateReferrals: affiliateReferralsQuery.isLoading,
    isLoadingAffiliateStats: affiliateStatsQuery.isLoading,
    isLoadingOptions: optionsQuery.isLoading,
    isUpdatingProfile: updateProfileMutation.isPending,
    isCreatingInvitation: createInvitationMutation.isPending,
    isCancellingInvitation: cancelInvitationMutation.isPending,
    isResendingInvitation: resendInvitationMutation.isPending,

    updateProfile,
    createInvitation,
    cancelInvitation,
    resendInvitation,
    refetchAll,
    isClientReferred
  }
})
